import React, { useCallback, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import _ from 'underscore';
import {
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogBody,
  DialogActions,
  Button,
  Input,
  mergeClasses,
} from '@fluentui/react-components';
import { Dismiss20Regular } from '@fluentui/react-icons';
import { validate } from '@bingads-webui-campaign-react/ads-studio-app-shared-components';

import { useModalStyle } from '../styles/edit-folder-name-modal-style';

const EditFolderNameModalImp = ({
  i18n,
  show,
  onComplete,
  onCancel,
  errorText,
  setErrorText,
  initName,
  title,
}) => {
  const classes = useModalStyle();
  const [name, setName] = useState('');

  const onChange = useCallback((event) => {
    const value = (event && event.target && event.target.value) || '';
    if (validate(value) && value.length <= 128) {
      setErrorText('');
      setName(value.trim());
    } else {
      setErrorText(i18n.getString(_TL_('You\'ve entered one or more invalid characters in the fields. Please correct them.')));
    }
  }, [setErrorText, i18n]);

  const onConfirm = useCallback(() => {
    setName('');
    onComplete(name);
  }, [name, onComplete]);

  const handleCancel = useCallback(() => {
    setName('');
    onCancel();
  }, [onCancel]);

  const onKeyDown = useCallback((event) => {
    if (event && event.key === 'Enter') {
      onConfirm();
    }
  }, [onConfirm]);

  const isSaveButtonDisabled = useMemo(
    () => (initName && name === initName) || !name || errorText,
    [errorText, initName, name]
  );

  const DismissIcon = useMemo(() => <Dismiss20Regular onClick={handleCancel} />, [handleCancel]);

  return (
    show ?
      <Dialog
        open={show}
        onDismiss={handleCancel}
      >
        <DialogSurface>
          <DialogBody className="folder-name-dialog">
            <DialogTitle className="folder-name-dialog-title" action={DismissIcon}>{title || i18n.getString(_TL_('Create folder'))}</DialogTitle>
            <DialogContent>
              <Input
                placeholder={i18n.getString(_TL_('Enter a name for your folder'))}
                className={mergeClasses('folder-name-input', classes.nameInput)}
                onChange={(event) => { onChange(event); }}
                onKeyDown={onKeyDown}
                autoFocus
                defaultValue={initName}
              />
              { errorText && <div className={classnames(classes.error, 'error-text')} >{errorText}</div> }
            </DialogContent>
            <DialogActions position="start">
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="primary" onClick={onConfirm} className={isSaveButtonDisabled ? '' : 'save-button'} disabled={isSaveButtonDisabled}>{i18n.getString(_TL_('Save'))}</Button>
              </DialogTrigger>
              <Button appearance="secondary" onClick={handleCancel}>{i18n.getString(_TL_('Cancel'))}</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog> : null
  );
};

EditFolderNameModalImp.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  show: PropTypes.bool.isRequired,
  onComplete: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  errorText: PropTypes.string,
  setErrorText: PropTypes.func,
  initName: PropTypes.string,
  title: PropTypes.string,
};

EditFolderNameModalImp.defaultProps = {
  errorText: '',
  setErrorText: _.noop,
  initName: '',
  title: undefined,
};

export const EditFolderNameModal = EditFolderNameModalImp;
